import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  Avatar,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  History as HistoryIcon,
  TrendingUp as TrendingIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { api } from '../services/api';

interface SearchSuggestion {
  id: number;
  name: string;
  slug: string;
  category: string;
  image?: string;
  type: 'product' | 'category' | 'recent' | 'trending';
}

interface SearchBarProps {
  placeholder?: string;
  variant?: 'outlined' | 'filled' | 'standard';
  size?: 'small' | 'medium';
  fullWidth?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = React.memo(({
  placeholder = "Keresés termékek között...",
  variant = 'outlined',
  size = 'medium',
  fullWidth = false,
}) => {
  const navigate = useNavigate();
  const { categories } = useSelector((state: RootState) => state.products);

  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Load recent searches from localStorage
    const saved = localStorage.getItem('recent_searches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    // Click outside handler
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (query: string) => {
    setLoading(true);
    try {
      const response = await api.get('/products/search-suggestions/', {
        params: { q: query }
      });

      const productSuggestions: SearchSuggestion[] = response.data.map((item: any) => ({
        id: item.id,
        name: item.name,
        slug: item.slug,
        category: item.category,
        image: item.image,
        type: 'product' as const,
      }));

      // Add category suggestions
      const categorySuggestions: SearchSuggestion[] = categories
        .filter(cat => cat.name.toLowerCase().includes(query.toLowerCase()))
        .map(cat => ({
          id: cat.id,
          name: cat.name,
          slug: cat.slug,
          category: '',
          type: 'category' as const,
        }));

      setSuggestions([...productSuggestions, ...categorySuggestions]);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [categories]);

  const getDefaultSuggestions = useCallback((): SearchSuggestion[] => {
    const recent: SearchSuggestion[] = recentSearches.slice(0, 3).map((term, index) => ({
      id: -index - 1,
      name: term,
      slug: '',
      category: '',
      type: 'recent',
    }));

    // Add some trending/popular categories
    const trending: SearchSuggestion[] = categories.slice(0, 3).map(cat => ({
      id: cat.id,
      name: cat.name,
      slug: cat.slug,
      category: '',
      type: 'trending',
    }));

    return [...recent, ...trending];
  }, [recentSearches, categories]);

  useEffect(() => {
    if (searchTerm.length >= 2) {
      // Debounce search suggestions
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        fetchSuggestions(searchTerm);
      }, 300);
    } else if (searchTerm.length === 0) {
      // Show recent searches and trending when empty
      setLoading(false);
      setSuggestions(getDefaultSuggestions());
    } else {
      setSuggestions([]);
      setLoading(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, fetchSuggestions, getDefaultSuggestions]);

  const handleSearch = (query: string) => {
    if (!query.trim()) return;

    // Save to recent searches
    const newRecentSearches = [query, ...recentSearches.filter(s => s !== query)].slice(0, 5);
    setRecentSearches(newRecentSearches);
    localStorage.setItem('recent_searches', JSON.stringify(newRecentSearches));

    // Navigate to products page with search
    navigate(`/termekek?search=${encodeURIComponent(query)}`);
    setShowSuggestions(false);
    setSearchTerm('');

    // Blur input on mobile
    inputRef.current?.blur();
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    switch (suggestion.type) {
      case 'product':
        navigate(`/termek/${suggestion.slug}`);
        break;
      case 'category':
        navigate(`/termekek?category=${suggestion.slug}`);
        break;
      case 'recent':
      case 'trending':
        setSearchTerm(suggestion.name);
        handleSearch(suggestion.name);
        break;
    }
    setShowSuggestions(false);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSuggestions(getDefaultSuggestions());
    inputRef.current?.focus();
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recent_searches');
    setSuggestions(getDefaultSuggestions());
  };

  const getSuggestionIcon = (type: string) => {
    switch (type) {
      case 'product':
        return <SearchIcon fontSize="small" />;
      case 'category':
        return <CategoryIcon fontSize="small" />;
      case 'recent':
        return <HistoryIcon fontSize="small" />;
      case 'trending':
        return <TrendingIcon fontSize="small" />;
      default:
        return <SearchIcon fontSize="small" />;
    }
  };

  const getSuggestionLabel = (type: string) => {
    switch (type) {
      case 'product':
        return 'Termék';
      case 'category':
        return 'Kategória';
      case 'recent':
        return 'Legutóbbi';
      case 'trending':
        return 'Népszerű';
      default:
        return '';
    }
  };

  return (
    <Box ref={searchRef} sx={{ position: 'relative', width: fullWidth ? '100%' : 'auto' }}>
      <TextField
        ref={inputRef}
        fullWidth={fullWidth}
        variant={variant}
        size={size}
        placeholder={placeholder}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => setShowSuggestions(true)}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            handleSearch(searchTerm);
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {loading && <CircularProgress size={20} />}
              {searchTerm && !loading && (
                <IconButton
                  size="small"
                  onClick={clearSearch}
                  edge="end"
                >
                  <ClearIcon />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'background.paper',
          },
        }}
      />

      {showSuggestions && (
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1300,
            maxHeight: 400,
            overflow: 'auto',
            mt: 0.5,
          }}
        >
          {suggestions.length > 0 ? (
            <List dense>
              {/* Recent searches header */}
              {recentSearches.length > 0 && suggestions.some(s => s.type === 'recent') && (
                <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Legutóbbi keresések
                  </Typography>
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ cursor: 'pointer' }}
                    onClick={clearRecentSearches}
                  >
                    Törlés
                  </Typography>
                </Box>
              )}

              {suggestions.map((suggestion, index) => (
                <ListItemButton
                  key={`${suggestion.type}-${suggestion.id}-${index}`}
                  onClick={() => handleSuggestionClick(suggestion)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1.5,
                  }}
                >
                  {suggestion.type === 'product' && suggestion.image ? (
                    <Avatar
                      src={suggestion.image}
                      alt={suggestion.name}
                      variant="rounded"
                      sx={{
                        width: 48,
                        height: 48,
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {getSuggestionIcon(suggestion.type)}
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                          {suggestion.name}
                        </Typography>
                        <Chip
                          label={getSuggestionLabel(suggestion.type)}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </Box>
                    }
                    secondary={suggestion.category || undefined}
                  />
                </ListItemButton>
              ))}

              {/* Show trending categories if no recent searches */}
              {recentSearches.length === 0 && suggestions.some(s => s.type === 'trending') && (
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Népszerű kategóriák
                  </Typography>
                </Box>
              )}
            </List>
          ) : searchTerm.length >= 2 && !loading ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Nincs találat a keresett kifejezésre
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Próbálj más kulcsszavakat
              </Typography>
            </Box>
          ) : searchTerm.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Írj be legalább 2 karaktert a kereséshez
              </Typography>
            </Box>
          ) : null}
        </Paper>
      )}
    </Box>
  );
});

SearchBar.displayName = 'SearchBar';

export default SearchBar;